import { useState, useEffect } from 'react';
import { Chip, Avatar } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { abbreviateNum } from 'lib/helpers';
import { Role } from 'lib/types';
import { User } from 'lib/types/users';
import Link from 'next/link';
import { modelToSlug } from 'lib/utils/modelSlugUtils';
import { compact } from 'lodash';
import { useRouter } from 'next/router';

interface Props {
  isProfileUserCurrentUser: boolean;
  profileUser: User;
  profileUserRoles: Role[];
}

export const KeyRoles = ({
  profileUser, isProfileUserCurrentUser, profileUserRoles,
}: Props) => {
  const API = useApi();
  const router = useRouter();
  const currentUser = useCurrentUser();
  const [editing, setEditing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [allUserRoles, setAllUserRoles] = useState<Role[]>(profileUserRoles);

  useEffect(() => {
    setAllUserRoles(profileUserRoles);
    setEditing(false);
  }, [profileUser?.id, currentUser, profileUserRoles]);

  const toggleKeyRole = (roleId: number) => {
    const roleIndex = allUserRoles.findIndex((r) => r.id === roleId);
    const role = allUserRoles[roleIndex];
    setAllUserRoles([
      ...allUserRoles.slice(0, roleIndex),
      {
        ...role,
        hidden: !role.hidden,
      },
      ...allUserRoles.slice(roleIndex + 1),
    ]);
  };

  const tryToAddKeyRole = async (roleId: number) => {
    try {
      await API.createHiddenKeyRole(roleId);
      toggleKeyRole(roleId);
    } catch (err) {
      enqueueSnackbar(`Error hiding role: ${err}`, {
        variant: 'error',
      });
    }
  };

  const tryToRemoveKeyRole = async (roleId: number) => {
    try {
      await API.deleteHiddenKeyRole(roleId);
      toggleKeyRole(roleId);
    } catch (err) {
      enqueueSnackbar(`Error unhiding role: ${err}`, {
        variant: 'error',
      });
    }
  };

  const handleClick = (role: Role) => (
    role.hidden
      ? tryToRemoveKeyRole(role.id as number)
      : tryToAddKeyRole(role.id as number)
  );

  // TODO(Jordan): Remove explicit backgroundColor: 'white' below. Needed for staging/prod.
  const listUserKeyRoles = () => allUserRoles.map((r: Role) => {
    if (r.hidden) {
      return null;
    }

    return (
      <Link
        key={r.id}
        href={{
          pathname: router.pathname,
          query: {
            role: modelToSlug(r, 'name'),
            profileuser: profileUser.username,
          },
        }}
      >
        <Chip
          className="bg-hintgray dark:bg-darkgray hover:bg-lightgray"
          sx={{ margin: '2px' }}
          key={`${r.id}-${r.total}`}
          avatar={<Avatar className="bg-white dark:bg-appbg-dark" sx={{ backgroundColor: 'white' }}>{abbreviateNum(r?.total || 0, 0)}</Avatar>}
          label={r.name}
        />
      </Link>
    );
  });

  const listAllUsersRoles = () => allUserRoles.map((r: Role) => (
    <Chip
      component="a"
      className="bg-hintgray dark:bg-darkgray"
      sx={{ margin: '2px' }}
      key={`${r.id}-${r.total}`}
      avatar={<Avatar className="bg-white dark:bg-appbg-dark" sx={{ backgroundColor: 'white' }}>{abbreviateNum(r?.total || 0, 0)}</Avatar>}
      onDelete={() => handleClick(r)}
      deleteIcon={r.hidden
        ? <VisibilityOff />
        : <Visibility />}
      label={r.name}
    />
  ));

  return (
    <div>
      {(isProfileUserCurrentUser || !!compact(listUserKeyRoles()).length) && (
        <h6 className="font-bold text-lg mb-2 text-center">
          Key Roles
        </h6>
      )}
      {isProfileUserCurrentUser
      && (
        <div className="mb-4">
          <button
            type="button"
            className="btn-primary-outlined"
            onClick={() => setEditing(!editing)}
          >
            {editing ? 'Done Editing' : 'Edit Key Roles'}
          </button>
        </div>
      )}
      {editing ? listAllUsersRoles() : listUserKeyRoles()}
    </div>
  );
};
